import React from 'react';

function formatMs(ms) {
  if (ms == null) return '—';
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function StatRow({ label, value, displayValue, max, color, warn, explain, dim }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;

  return (
    <div className={`stat-row${dim ? ' stat-row--dim' : ''}`}>
      <div className="stat-row__line">
        <span className="stat-row__label">{label}</span>
        <div className="stat-row__track" aria-hidden="true">
          <div
            className="stat-row__fill"
            style={{ width: `${pct}%`, '--bar-color': color }}
          />
        </div>
        <span className={`stat-row__val${warn ? ' stat-row__val--warn' : ''}`}>
          {displayValue}
        </span>
        {explain && (
          <button
            className="stat-row__help"
            data-tooltip={explain}
            aria-label={`Explain ${label}`}
            type="button"
          >
            ?
          </button>
        )}
      </div>
    </div>
  );
}

export default function EpisodeStats({
  turnSwitches, avgTurnMs, longestMonologueMs,
  hostQuestions, guestQuestions, hostLabel = 'Host', guestLabel = 'Guest',
  showQuestions = true,
}) {
  const hasConversation = turnSwitches != null;
  // Whether the split is meaningful for this episode's cast is decided by
  // `showsQuestionSplit` in api.js, so every page asks it the same way.
  const hasQuestions    = showQuestions && hostQuestions != null && (hostQuestions > 0 || guestQuestions > 0);

  if (!hasConversation && !hasQuestions) return null;

  const totalQ = (hostQuestions ?? 0) + (guestQuestions ?? 0);

  return (
    <div className="episode-stats" aria-label="Episode conversation statistics">
      {hasConversation && (
        <>
          <StatRow
            label="Dialog switches"
            value={turnSwitches}
            displayValue={turnSwitches}
            max={1000}
            color="var(--palette-violet)"
            explain="How many times the conversation changes speaker. More switches means a faster back-and-forth pace."
          />
          <StatRow
            label="Avg turn"
            value={avgTurnMs}
            displayValue={formatMs(avgTurnMs)}
            max={300_000}
            color="var(--palette-teal)"
            explain="Average length of each uninterrupted speaking stretch. Shorter means more rapid exchange."
          />
          <StatRow
            label="Longest speech"
            value={longestMonologueMs}
            displayValue={formatMs(longestMonologueMs)}
            max={1_800_000}
            color={longestMonologueMs > 300_000 ? 'var(--palette-amber)' : 'var(--palette-coral)'}
            warn={longestMonologueMs > 300_000}
            explain="The longest anyone spoke without being interrupted."
          />
        </>
      )}
      {hasQuestions && (
        <>
          <StatRow
            label={`${hostLabel} questions`}
            value={hostQuestions}
            displayValue={hostQuestions}
            max={Math.max(totalQ, 1)}
            color="var(--palette-sky)"
            explain="Number of question marks in host utterances. A higher count means a more inquisitive interviewer."
          />
          <StatRow
            label={`${guestLabel} questions`}
            value={guestQuestions}
            displayValue={guestQuestions}
            max={Math.max(totalQ, 1)}
            color="var(--palette-lime)"
            dim={guestQuestions === 0}
            explain="Question marks in guest utterances. Story-heavy guests score high here due to rhetorical questions."
          />
        </>
      )}
    </div>
  );
}
